/*
App address:
https://heroku-react-graderai-20250506-d782aea9bc2c.herokuapp.com/

Local URL:
http://localhost:3000

Terminal command to build and run:
  
npm install (first time only)
npm run build
npm start (run local development server)


*/

// App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';

import DownloadPapers from './download-papers';
// import WhatsAppWidget from './WhatsAppWidget';

import TeacherPdfUploader from "./TeacherPdfUploader";
import StudentPdfUploader from "./StudentPdfUploader";
import TeacherStudentSubmissions from './TeacherStudentSubmissions';
import TeacherLoginPage from './TeacherLoginPage';
import HomePage from './HomePage';
import RequireTeacherAuth from "./RequireTeacherAuth";
import ContactPage from "./ContactPage";
import AboutPage from "./AboutPage";
import OralExamVoicePage from "./OralExamVoicePage";
import DsaInterviewVoicePage from "./DsaInterviewVoicePage";
import PublicOralExamEmbedPage from "./PublicOralExamEmbedPage";
import InterviewPracticePage from "./InterviewPracticePage";
import PublicDsaInterviewEmbedPage from "./PublicDsaInterviewEmbedPage";
import PublicWritingGraderPage from "./PublicWritingGraderPage";
import PublicAssessmentReportPage from "./PublicAssessmentReportPage";
import PublicAssessmentSubmitPage from "./PublicAssessmentSubmitPage";
import AdminLoginPage from "./AdminLoginPage";
import AdminRequireAuth from "./AdminRequireAuth";
import AdminLayout from "./AdminLayout";
import AdminDashboardHome from "./AdminDashboardHome";
import AdminUserLookupPage from "./AdminUserLookupPage";
import AdminChatPage from "./AdminChatPage";
import AdminAssistantConversationsPage from "./AdminAssistantConversationsPage";
import AdminCorporateUsagePage from "./AdminCorporateUsagePage";
import AdminCorporateTeachersPage from "./AdminCorporateTeachersPage";
import AdminCorporateSchoolsPage from "./AdminCorporateSchoolsPage";
import AdminCreditActivityPage from "./AdminCreditActivityPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import CorporateSignupPage from "./CorporateSignupPage";
import CorporateSignupEmbedPage from "./CorporateSignupEmbedPage";
import PublicDashboardEmbedPage from "./PublicDashboardEmbedPage";
import ParagraphPracticePage from "./ParagraphPracticePage";
import ParagraphPracticeEmbedPage from "./ParagraphPracticeEmbedPage";
import StudyBuddyPage from "./StudyBuddyPage";
import StudyBuddyEmbedPage from "./StudyBuddyEmbedPage";
import StudyBuddyPracticePage from "./StudyBuddyPracticePage";
import StudyBuddyPracticeEmbedPage from "./StudyBuddyPracticeEmbedPage";
import StudyBuddyPracticeV2Page from "./StudyBuddyPracticeV2Page";
import StudyBuddyPracticeV2EmbedPage from "./StudyBuddyPracticeV2EmbedPage";
import AdminStudyBuddyPublishedPacksPage from "./AdminStudyBuddyPublishedPacksPage";
import PracticeSimulatorPage from "./PracticeSimulatorPage";
import PracticeSimulatorEmbedPage from "./PracticeSimulatorEmbedPage";
import PracticeSimulatorVoicePage from "./PracticeSimulatorVoicePage";
import PracticeSimulatorVoiceEmbedPage from "./PracticeSimulatorVoiceEmbedPage";

function AppContent({ handleLogout }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const pathname = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const isEmbedMode =
    pathname === "/oral-exam-voice-embed" ||
    pathname === "/dsa-interview-voice-embed" ||
    pathname === "/corporate-signup-embed" ||
    pathname === "/public-dashboard-embed" ||
    pathname === "/paragraph-writing-practice-embed" ||
    pathname === "/study-buddy-embed" ||
    pathname === "/study-buddy-practice-embed" ||
    pathname === "/study-buddy-practice-v2-embed" ||
    pathname === "/practice-simulator-embed" ||
    pathname === "/practice-simulator-practice-embed" ||
    params.get("embed") === "true";
  const isStandalonePage = pathname === "/verify-email";

  if (isAdminRoute) {
    return (
      <div className="App">
        <Routes>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin"
            element={
              <AdminRequireAuth>
                <AdminLayout />
              </AdminRequireAuth>
            }
          >
            <Route index element={<AdminDashboardHome />} />
            <Route path="users" element={<AdminUserLookupPage />} />
            <Route path="credit-activity" element={<AdminCreditActivityPage />} />
            <Route path="chat" element={<AdminChatPage />} />
            <Route path="assistant-conversations" element={<AdminAssistantConversationsPage />} />
            <Route path="corporate-usage" element={<AdminCorporateUsagePage />} />
            <Route path="corporate-teachers" element={<AdminCorporateTeachersPage />} />
            <Route path="corporate-schools" element={<AdminCorporateSchoolsPage />} />
            <Route path="study-buddy-published-packs" element={<AdminStudyBuddyPublishedPacksPage />} />
          </Route>
        </Routes>
      </div>
    );
  }

  const appRoutes = (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/teacher-login" element={<TeacherLoginPage />} />

      <Route
        path="/teacher-upload-pdf"
        element={
          <RequireTeacherAuth>
            {(session) => <TeacherPdfUploader session={session} />}
          </RequireTeacherAuth>
        }
      />

      <Route path="/student-upload-pdf" element={<StudentPdfUploader />} />
      <Route
        path="/teacher-student-submissions"
        element={
          <RequireTeacherAuth>
            {(session) => <TeacherStudentSubmissions session={session} />}
          </RequireTeacherAuth>
        }
      />

      <Route path="/download-papers" element={<DownloadPapers />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/assessment-submit" element={<PublicAssessmentSubmitPage />} />
      <Route
        path="/assessment-report/:submissionId"
        element={<PublicAssessmentReportPage />}
      />
      <Route path="/oral-exam-voice" element={<OralExamVoicePage />} />
      <Route path="/dsa-interview-voice" element={<DsaInterviewVoicePage />} />
      <Route
        path="/dsa-interview-voice-public"
        element={<PublicDsaInterviewEmbedPage />}
      />
      <Route
        path="/dsa-interview-voice-embed"
        element={<PublicDsaInterviewEmbedPage />}
      />
      <Route
        path="/oral-exam-voice-public"
        element={<PublicOralExamEmbedPage />}
      />
      <Route path="/interview-practice" element={<InterviewPracticePage />} />
      <Route
        path="/oral-exam-voice-embed"
        element={<PublicOralExamEmbedPage />}
      />
      <Route
        path="/public-writing-grader"
        element={<PublicWritingGraderPage />}
      />
      <Route
        path="/corporate-signup"
        element={<CorporateSignupPage />}
      />
      <Route
        path="/corporate-signup-embed"
        element={<CorporateSignupEmbedPage />}
      />
      <Route
        path="/public-dashboard-embed"
        element={<PublicDashboardEmbedPage />}
      />
      <Route path="/paragraph-writing-practice" element={<ParagraphPracticePage />} />
      <Route path="/paragraph-writing-practice-embed" element={<ParagraphPracticeEmbedPage />} />
      <Route path="/study-buddy" element={<StudyBuddyPage />} />
      <Route path="/study-buddy-embed" element={<StudyBuddyEmbedPage />} />
      <Route path="/study-buddy-practice" element={<StudyBuddyPracticePage />} />
      <Route path="/study-buddy-practice-embed" element={<StudyBuddyPracticeEmbedPage />} />
      {/* V1 routes remain registered but unlinked for rollback or future continuous conversation. */}
      <Route path="/study-buddy-practice-v2" element={<StudyBuddyPracticeV2Page />} />
      <Route path="/study-buddy-practice-v2-embed" element={<StudyBuddyPracticeV2EmbedPage />} />
      <Route path="/practice-simulator" element={<PracticeSimulatorPage />} />
      <Route path="/practice-simulator-embed" element={<PracticeSimulatorEmbedPage />} />
      <Route path="/practice-simulator-practice" element={<PracticeSimulatorVoicePage />} />
      <Route path="/practice-simulator-practice-embed" element={<PracticeSimulatorVoiceEmbedPage />} />
    </Routes>
  );

  if (isEmbedMode || isStandalonePage) {
    return (
      <div className="App App-embed">
        <main className="App-main-embed">{appRoutes}</main>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="app-logo">
          <img
            src="https://chatbotmia.blob.core.windows.net/assets/Articulate%20export_Logo%20-%20Full%20Color_NoBG_trimmed.PNG"
            alt="Articulate"
          />
        </div>

        <nav>
          <Link to="/">Home</Link> |{" "}
          <Link to="/about">About</Link> |{" "}
          <Link to="/teacher-login">Teacher Login</Link> |{" "}
          <Link to="/teacher-upload-pdf">Teacher - Upload Question</Link> |{" "}
          <Link to="/teacher-student-submissions">Teacher - View Student Submissions</Link> |{" "}
          <Link to="/student-upload-pdf">Student - Upload Script</Link> |{" "}
          <Link to="/oral-exam-voice">Oral Exam Voice</Link> |{" "}
          <Link to="/corporate-signup">Corporate Signup</Link> |{" "}
          <Link to="/contact">Contact</Link> |{" "}
         
          <button
            type="button"
            onClick={handleLogout}
            style={{
              background: "none",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              font: "inherit",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Logout
          </button>
        </nav>
        {appRoutes}
      </header>

      <footer style={{ fontSize: '0.8rem', marginTop: '2rem', color: '#ccc' }}>
        <a href="/about" style={{ color: '#ccc' }}>About</a> |{" "}
        <a
          href="https://articulate.com.sg/terms-and-conditions/"
          target="_blank"
          rel="noreferrer"
          style={{ color: '#ccc' }}
        >
          Terms and Conditions
        </a>{" "}
        |{" "}
        <a
          href="https://articulate.com.sg/privacy-policy/"
          target="_blank"
          rel="noreferrer"
          style={{ color: '#ccc' }}
        >
          Privacy Policy
        </a>
      </footer>

      {/* <WhatsAppWidget
        phoneNumber="16079009037"
        message="Hi! I have a question."
      /> */}
    </div>
  );
}

function App() {
  useEffect(() => {
    if (!localStorage.getItem("cookie-consent")) {
      alert("We use cookies to keep you logged in. By continuing, you agree to our use of cookies.");
      localStorage.setItem("cookie-consent", "true");
    }
  }, []);

  async function handleLogout() {
    try {
      await fetch("/api/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      // Still redirect home even if the logout request fails.
    } finally {
      window.location.href = "/";
    }
  }

  return (
    <Router>
      <AppContent handleLogout={handleLogout} />
    </Router>
  );
}

export default App;
